import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';

const CLASS_TYPES = ['ASD', 'Voith', 'Conventional', 'Tractor'];
const EMPTY_ASSET = {
  name: '',
  classType: 'ASD',
  bollardPullTons: '',
  homeStationId: '',
  captainRoster: '',
};

export default function AffiliatePortalPage() {
  const [profile, setProfile] = useState(null);
  const [assets, setAssets] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [phone, setPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ASSET);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [profileRes, assetsRes, stationsRes] = await Promise.all([
      api.getAffiliateProfile(),
      api.listAffiliateAssets(),
      api.getAffiliateStations(),
    ]);
    setProfile(profileRes);
    setPhone(profileRes?.dispatch_phone || '');
    setAssets(assetsRes || []);
    setStations(stationsRes || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load the affiliate portal.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setPageMsg(null);
    try {
      await api.updateAffiliateProfile({ dispatchPhone: phone.trim() });
      setPageMsg({ type: 'success', text: 'Operator profile updated.' });
      await load();
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to update profile.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const openRegister = () => {
    setFormError('');
    setForm(EMPTY_ASSET);
    setModalOpen(true);
  };

  const saveAsset = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.name.trim()) {
      setFormError('Tug asset name is required.');
      return;
    }
    if (!form.bollardPullTons || Number(form.bollardPullTons) <= 0) {
      setFormError('Bollard pull must be a positive number.');
      return;
    }
    setSaving(true);
    try {
      await api.createAffiliateAsset({
        name: form.name.trim(),
        classType: form.classType,
        bollardPullTons: Number(form.bollardPullTons),
        homeStationId: form.homeStationId || null,
        captainRoster: form.captainRoster.trim() || null,
      });
      setPageMsg({ type: 'success', text: 'Affiliate asset added to the capacity register.' });
      await load();
      setModalOpen(false);
    } catch (err) {
      setFormError(err.message || 'Failed to save asset.');
    } finally {
      setSaving(false);
    }
  };

  const removeAsset = async (asset) => {
    setPageMsg(null);
    try {
      await api.deleteAffiliateAsset(asset.id);
      await load();
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to delete asset.' });
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="affiliate-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-handshake me-2" aria-hidden="true" />
                Affiliate Portal &amp; Fleet Register
              </h3>
              <p className="text-muted mb-0 small">
                Register your fleet assets and captains as available affiliate capacity.
              </p>
            </div>
            <button className="btn btn-primary" onClick={openRegister} data-testid="register-asset">
              <i className="fas fa-plus me-1" aria-hidden="true" />
              Register Fleet Asset
            </button>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="affiliate-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="affiliate-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* LEFT: affiliate fleet */}
          <div className="col-lg-7 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-list-ul me-2" aria-hidden="true" />
                  Affiliate Capacity Fleet
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Asset Name</th>
                        <th>Class / Type</th>
                        <th>Bollard Pull</th>
                        <th>Home Station</th>
                        <th>Status</th>
                        <th aria-label="action" />
                      </tr>
                    </thead>
                    <tbody>
                      {assets.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            No affiliate assets registered yet.
                          </td>
                        </tr>
                      )}
                      {assets.map((a) => (
                        <tr key={a.id} data-testid="asset-row" data-name={a.name}>
                          <td className="fw-bold">
                            {a.name}
                            {a.captain_roster ? (
                              <span className="d-block text-muted small">
                                <i className="fas fa-user me-1" aria-hidden="true" />
                                {a.captain_roster}
                              </span>
                            ) : null}
                          </td>
                          <td>{a.class_type}</td>
                          <td>{Number(a.bollard_pull_tons)} T</td>
                          <td className="small">{a.home_station_name || '—'}</td>
                          <td>
                            <span className="badge bg-success">{a.active_status}</span>
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeAsset(a)}
                              aria-label={`Delete ${a.name}`}
                            >
                              <i className="fas fa-trash" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: operator profile */}
          <div className="col-lg-5 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-id-card me-2" aria-hidden="true" />
                  Operator Profile
                </h5>
              </div>
              <div className="card-body">
                <form onSubmit={saveProfile} noValidate>
                  <div className="mb-3">
                    <label className="form-label small fw-bold text-muted">Operator Business Name</label>
                    <input className="form-control form-control-sm" value={profile?.company_name || ''} readOnly />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold text-muted">Pre-agreed SAP Partner ID</label>
                    <input className="form-control form-control-sm" value={profile?.sap_partner_id || '—'} readOnly />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="aff-phone" className="form-label small fw-bold text-muted">
                      Dispatch Desk Phone
                    </label>
                    <input
                      id="aff-phone"
                      className="form-control form-control-sm"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      data-testid="profile-phone"
                    />
                  </div>
                  <button className="btn btn-primary btn-sm w-100" disabled={savingProfile} data-testid="save-profile">
                    <i className="fas fa-save me-1" aria-hidden="true" />
                    Save Profile Settings
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Register asset modal */}
      {modalOpen && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="asset-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={saveAsset} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-ship me-2 text-primary" aria-hidden="true" />
                      Register Fleet Asset
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setModalOpen(false)} />
                  </div>
                  <div className="modal-body">
                    {formError && (
                      <div className="alert alert-danger" role="alert" data-testid="asset-error">
                        {formError}
                      </div>
                    )}
                    <div className="mb-3">
                      <label htmlFor="a-name" className="form-label small fw-bold text-muted">
                        Tug Asset Name <span className="text-danger">*</span>
                      </label>
                      <input
                        id="a-name"
                        className="form-control form-control-sm"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="asset-name"
                      />
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="a-class" className="form-label small fw-bold text-muted">
                          Class / Type <span className="text-danger">*</span>
                        </label>
                        <select
                          id="a-class"
                          className="form-select form-select-sm"
                          value={form.classType}
                          onChange={(e) => setForm({ ...form, classType: e.target.value })}
                          data-testid="asset-class"
                        >
                          {CLASS_TYPES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="a-bollard" className="form-label small fw-bold text-muted">
                          Bollard Pull (Tons) <span className="text-danger">*</span>
                        </label>
                        <input
                          id="a-bollard"
                          type="number"
                          step="0.1"
                          className="form-control form-control-sm"
                          value={form.bollardPullTons}
                          onChange={(e) => setForm({ ...form, bollardPullTons: e.target.value })}
                          data-testid="asset-bollard"
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="a-station" className="form-label small fw-bold text-muted">
                        Home Station / Base
                      </label>
                      <select
                        id="a-station"
                        className="form-select form-select-sm"
                        value={form.homeStationId}
                        onChange={(e) => setForm({ ...form, homeStationId: e.target.value })}
                        data-testid="asset-station"
                      >
                        <option value="">— Select —</option>
                        {stations.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-1">
                      <label htmlFor="a-roster" className="form-label small fw-bold text-muted">
                        Active Captain Roster (contacts)
                      </label>
                      <textarea
                        id="a-roster"
                        rows={2}
                        className="form-control form-control-sm"
                        value={form.captainRoster}
                        onChange={(e) => setForm({ ...form, captainRoster: e.target.value })}
                        placeholder="e.g. Capt. Reyes +63 917 555 1234"
                        data-testid="asset-roster"
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setModalOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="save-asset">
                      {saving && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-save me-1" aria-hidden="true" />
                      Save Affiliate Asset
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
